import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ariaControls", "current", "onSearchChange", "search", "searchClassName"],
  _excluded2 = ["autoFilter", "hideResults", "itemComponent", "minimumSearchCount", "onSearchChange", "options", "resultsClassName", "search", "searchClassName", "style", "enableKeyboardScrollingNav"];
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import { OptionOrGroupType, SingleValueType, isOptionGroup } from '../types/OptionTypes';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import partial from 'react-utils/partial';
import classNames from 'classnames';
import SyntheticEvent from '../core/SyntheticEvent';
import { getNoMatchesOption, getSearchRegExp } from './UITypeaheadUtils';
import UITypeaheadResults from './results/UITypeaheadResults';
import UITypeaheadResultsGroup from './results/UITypeaheadResultsGroup';
// @ts-expect-error Convert to TS
import UITypeaheadResultsItem from './results/UITypeaheadResultsItem';
import UITypeaheadSearch from './search/UITypeaheadSearch';
import { uniqueId } from '../utils/underscore';
import refObject from '../utils/propTypes/refObject';

/** @internal */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getFlattenedOptions(groupedOptions) {
  const groups = [];
  const options = [];
  groupedOptions.forEach(option => {
    if (isOptionGroup(option)) {
      const groupIdx = groups.push(option) - 1;
      option.options.forEach(groupOption => {
        options.push(Object.assign({}, groupOption, {
          groupIdx
        }));
      });
    } else {
      options.push(Object.assign({}, option, {
        groupIdx: -1
      }));
    }
  });
  return {
    options,
    groups
  };
}
function getHighlightedIndex(component, filtered) {
  return filtered.options.indexOf(component.getHighlighted(filtered));
}
const KeyHandlers = {
  ArrowDown: component => {
    const filtered = component.getFilteredOptions();
    const {
      options
    } = filtered;
    const nextIndex = (getHighlightedIndex(component, filtered) + 1) % options.length;
    component.setState({
      highlighted: options[nextIndex],
      highlightedId: component.getResultItemId(options[nextIndex])
    });
  },
  // The KeyDown handler that uses these handlers expects a KeyBoardEvent,
  // but the handleChange method expects a MouseEvent.
  Enter: (component, evt) => {
    component.handleChange(component.getHighlighted(component.getFilteredOptions()), evt);
  },
  ArrowUp: component => {
    const filtered = component.getFilteredOptions();
    const {
      options
    } = filtered;
    const index = getHighlightedIndex(component, filtered);
    const nextIndex = index === 0 ? options.length - 1 : index - 1;
    component.setState({
      highlighted: options[nextIndex],
      highlightedId: component.getResultItemId(options[nextIndex])
    });
  }
};
class UITypeaheadCore extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    let derivedState = {};
    if (props.options !== state.prevOptions) {
      derivedState = Object.assign({}, derivedState, {
        flattened: getFlattenedOptions(props.options),
        prevOptions: props.options
      });
    }
    if (state.prevAutoFilter && props.search !== state.prevSearch) {
      derivedState = Object.assign({}, derivedState, {
        searchRegex: props.search && getSearchRegExp(props.search),
        prevSearch: props.search
      });
    } else if (props.search !== state.prevSearch) {
      derivedState = Object.assign({}, derivedState, {
        prevSearch: props.search
      });
    }
    if (props.autoFilter !== state.prevAutoFilter) {
      derivedState = Object.assign({}, derivedState, {
        prevAutoFilter: props.autoFilter
      });
    }
    return derivedState;
  }
  constructor(props) {
    super(props);
    this.handleChange = (option, evt) => {
      if (option.disabled) {
        return;
      }
      this.handleHighlight(option, evt);
      const {
        onChange
      } = this.props;
      if (typeof onChange === 'function') {
        onChange(SyntheticEvent(option.value, evt));
      }
    };
    this.handleHighlight = (option, evt) => {
      const {
        highlighted
      } = this.state;
      if (highlighted === option) {
        return;
      }
      this.setState({
        highlighted: option,
        highlightedId: evt.currentTarget.id
      });
    };
    this.handleKeyDown = evt => {
      const {
        key
      } = evt;
      const handler = KeyHandlers[key] ? KeyHandlers[key] : null;
      if (handler) {
        evt.preventDefault();
        handler(this, evt);
      }
    };
    const {
      autoFilter,
      options,
      search
    } = props;
    this.state = {
      flattened: getFlattenedOptions(options),
      highlighted: null,
      highlightedId: null,
      prevAutoFilter: props.autoFilter,
      prevOptions: props.options,
      prevSearch: props.search,
      searchRegex: search && autoFilter ? getSearchRegExp(search) : undefined
    };
    this._highlightedRef = /*#__PURE__*/createRef();
  }
  componentDidUpdate() {
    const highlightedNode = this._highlightedRef.current;
    if (!highlightedNode) {
      return;
    }
    const {
      offsetHeight,
      offsetParent,
      offsetTop
    } = highlightedNode;
    if (!offsetParent) return;
    const scrollTop = offsetParent.scrollTop;
    if (scrollTop > offsetTop) {
      offsetParent.scrollTop = offsetTop;
    }
    const nodeBottom = offsetHeight + offsetTop;
    const parentBottom = scrollTop + offsetParent.offsetHeight;
    if (parentBottom >= nodeBottom) {
      return;
    }
    offsetParent.scrollTop = scrollTop + nodeBottom - parentBottom;
  }
  getClassName() {
    return classNames("private-dropdown private-typeahead", this.props.className, this.props.minimumSearchCount && this.getSearchCount() < this.props.minimumSearchCount && 'hide-search');
  }
  getFilteredOptions() {
    const {
      searchRegex,
      flattened
    } = this.state;
    const results = searchRegex ? flattened.options.filter(({
      dropdownText,
      text,
      value
    }) => text && searchRegex.test(text) || searchRegex.test(`${value}`) || typeof dropdownText === 'string' && searchRegex.test(dropdownText)) : flattened.options;
    return {
      groups: flattened.groups,
      options: results.length ? results : [getNoMatchesOption()]
    };
  }
  getHighlighted(filtered) {
    const {
      value
    } = this.props;
    const {
      highlighted
    } = this.state;
    if (highlighted && filtered.options.indexOf(highlighted) > -1) {
      return highlighted;
    }
    return filtered.options.find(option => option.value === value) || filtered.options[0] || null;
  }
  getResultItemId(option) {
    return `${this._id}_${this.state.flattened.options.indexOf(option)}`;
  }
  getSearchCount() {
    const {
      flattened
    } = this.state;
    if (!flattened || !flattened.options) {
      return -1;
    }
    return flattened.options.length + flattened.groups.length;
  }
  renderFilteredOptions() {
    const filtered = this.getFilteredOptions();
    const {
      groups
    } = filtered;
    const highlighted = this.getHighlighted(filtered);
    let groupIdx = -1;
    let groupedOptions = [];
    const options = [];
    filtered.options.forEach(option => {
      if (option.groupIdx !== groupIdx) {
        if (groupedOptions.length) {
          options.push(this.renderResultsGroup(groups[groupIdx], groupedOptions, highlighted, this.props.enableKeyboardScrollingNav));
        }
        groupIdx = option.groupIdx || 0;
        groupedOptions = [];
      }
      if (option.groupIdx === -1) {
        options.push(this.renderResultsItem(option, highlighted, this.props.enableKeyboardScrollingNav));
      } else {
        groupedOptions.push(option);
      }
    });
    if (groupedOptions.length) {
      options.push(this.renderResultsGroup(groups[groupIdx], groupedOptions, highlighted, this.props.enableKeyboardScrollingNav));
    }
    return options;
  }
  renderResults({
    id,
    hideResults,
    resultsClassName
  }) {
    if (hideResults) {
      return null;
    }
    return /*#__PURE__*/_jsx(UITypeaheadResults, {
      id: id,
      className: resultsClassName,
      children: this.renderFilteredOptions()
    });
  }
  renderResultsGroup(group, options, highlighted, enableKeyboardScrollingNav) {
    return /*#__PURE__*/_jsx(UITypeaheadResultsGroup, {
      group: group,
      children: options.map(option => this.renderResultsItem(option, highlighted, enableKeyboardScrollingNav))
    }, String(group.value || group.options[0].value));
  }
  renderResultsItem(option, highlighted, enableKeyboardScrollingNav) {
    if (option === getNoMatchesOption()) {
      return this.renderResultsEmpty();
    }
    const isHighlighted = highlighted === option;
    return /*#__PURE__*/_jsx(UITypeaheadResultsItem, {
      highlighted: isHighlighted,
      itemComponent: this.props.itemComponent,
      onClick: partial(this.handleChange, option),
      onMouseEnter: partial(this.handleHighlight, option),
      option: option,
      ref: isHighlighted ? this._highlightedRef : undefined,
      search: this.state.searchRegex,
      id: this.getResultItemId(option),
      enableKeyboardScrollingNav: enableKeyboardScrollingNav
    }, String(option.value));
  }
  renderResultsEmpty() {
    return /*#__PURE__*/_jsx(UITypeaheadResultsItem, {
      disabled: true,
      itemComponent: this.props.itemComponent,
      option: getNoMatchesOption()
    }, String(getNoMatchesOption().value));
  }
  renderSearch(_ref) {
    let {
        ariaControls,
        current,
        onSearchChange,
        search,
        searchClassName
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(UITypeaheadSearch, Object.assign({}, rest, {
      "aria-activedescendant": current,
      "aria-controls": ariaControls,
      className: searchClassName,
      onChange: onSearchChange,
      onKeyDown: this.handleKeyDown,
      value: search
    }));
  }
  render() {
    const _this$props = this.props,
      {
        autoFilter: __autoFilter = UITypeaheadCore.defaultProps.autoFilter,
        hideResults = UITypeaheadCore.defaultProps.hideResults,
        itemComponent: __itemComponent = UITypeaheadCore.defaultProps.itemComponent,
        minimumSearchCount: __minimumSearchCount = UITypeaheadCore.defaultProps.minimumSearchCount,
        onSearchChange,
        resultsClassName,
        search = UITypeaheadCore.defaultProps.search,
        searchClassName,
        style
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const {
      highlightedId
    } = this.state;
    this._id = this._id || uniqueId('typeahead-');
    const renderedSearch = this.renderSearch(Object.assign({
      ariaControls: this._id,
      current: highlightedId || undefined,
      onSearchChange,
      search,
      searchClassName
    }, rest));
    const renderedResults = this.renderResults({
      id: this._id,
      hideResults,
      resultsClassName
    });
    return /*#__PURE__*/_jsxs("div", {
      "aria-expanded": true,
      "aria-haspopup": "listbox",
      "aria-controls": this._id,
      className: this.getClassName(),
      role: "combobox",
      style: style,
      children: [renderedSearch, renderedResults]
    });
  }
}
UITypeaheadCore.propTypes = {
  autoFilter: PropTypes.bool,
  hideResults: PropTypes.bool,
  inputRef: refObject,
  itemComponent: UITypeaheadResultsItem.propTypes.itemComponent,
  minimumSearchCount: PropTypes.number,
  onChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  // @ts-expect-error Proptypes for group and option are misaligned with TS
  options: PropTypes.arrayOf(OptionOrGroupType.isRequired).isRequired,
  placeholder: PropTypes.string,
  resultsClassName: PropTypes.string,
  search: PropTypes.string,
  searchClassName: PropTypes.string,
  value: SingleValueType,
  /**
   * This prop is used to fix an issue with the `UITypehead`'s container not
   * being able to scroll using arrow keys when a custom `itemComponent` is
   * provided. To enable the fix, set this prop to `true` and do not use `li`
   * as the root element of your custom `itemComponent`.
   */
  enableKeyboardScrollingNav: PropTypes.bool
};
UITypeaheadCore.defaultProps = {
  autoFilter: true,
  hideResults: false,
  itemComponent: 'li',
  minimumSearchCount: 0,
  search: '',
  enableKeyboardScrollingNav: false
};
UITypeaheadCore.displayName = 'UITypeahead';
const UITypeahead = ShareInput(Controllable(UITypeaheadCore, ['search', 'value']));

// @ts-expect-error noMatchesI18nKey does not exist on ShareInput
UITypeahead.noMatchesI18nKey = 'salesUI.UITypeahead.noMatchesFound';
// @ts-expect-error noOptionsI18nKey does not exist on ShareInput
UITypeahead.noOptionsI18nKey = 'salesUI.UITypeahead.noOptions';
export default UITypeahead;